/**
 * Whether the owner can read a conversation in this console, for a page whose way back leads to one.
 *
 * The console draws the conversations it speaks in. One the Agent holds for itself (the operating loop's, a
 * schedule's) has words nobody here is shown, so a button "back to the conversation" opens an empty page. Only
 * the server knows which is which (`/api/chat/<id>/drawn`), so a page asks and leads to the Agent's workspace
 * when the answer is no. Nothing here names a plugin or a conversation.
 *
 * `undefined` is "not answered yet": the page draws nothing that leads there, or draws it without moving what
 * is around it, until it is. An answer that fails counts as yes -- no way back is taken away over it.
 */

import { useEffect, useState } from "react";
import { api } from "./api";

/** An answer that does not come is no reason to take a way back from the owner: it stays as it was. */
async function ask(session: string): Promise<boolean> {
  try { return (await api.conversationDrawn(session)).owner_conversation !== false; } catch { return true; }
}

/** The answer for each conversation named, as it comes; one not answered yet is absent. */
export function useConversationsDrawn(sessions: readonly (string | undefined)[]): Record<string, boolean | undefined> {
  const wanted = JSON.stringify([...new Set(sessions.filter((session): session is string => Boolean(session)))].sort());
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let live = true;
    for (const session of JSON.parse(wanted) as string[]) {
      void ask(session).then(drawn => {
        if (live) setAnswers(held => held[session] === drawn ? held : { ...held, [session]: drawn });
      });
    }
    return () => { live = false; };
  }, [wanted]);
  return answers;
}

export function useConversationDrawn(session: string | undefined): boolean | undefined {
  return useConversationsDrawn([session])[session ?? ""];
}
